/**
 * LA CRÉATION — l'écran le plus important du jeu après celui de lecture (design §3).
 *
 * Huit points, définitifs. Aucune progression ne viendra rattraper une répartition ratée : le §Q8
 * du brief supprime les niveaux, et c'est ce qui rend ce seul écran irréversible. Le banc de mesure
 * l'a chiffré — un Clerc qui met ses points en Vie et en Essence gagne 68 % de ses combats de
 * palier ; le même Clerc, points en Attaque, en gagne 93 %.
 *
 * D'OÙ LA RÈGLE TENUE ICI : **la conséquence de chaque point s'affiche AVANT qu'il ne soit
 * dépensé.** Pas « +1 Attaque », mais ce que +1 Attaque fait au jet, aux dégâts et au dos. Un
 * joueur qui découvre son erreur au troisième combat a perdu son héros pour une raison qu'on ne lui
 * avait pas dite — et il ne reviendra pas.
 *
 * Les nombres affichés viennent tous de `/public/regles` : le barème est dans `regles.ts` côté
 * moteur, jamais recopié ici.
 */
import { useMemo, useState } from "react";

import { api } from "../api/client.js";
import { useRessource } from "../api/chargement.js";
import type { Caracteristique, FicheArchetype, Regles, Stats } from "../api/types.js";
import { Glyphe } from "../composants/Glyphe.js";
import { Message } from "../composants/Message.js";
import { Filet, Parchemin } from "../composants/Parchemin.js";
import { messageDe } from "../lexique/erreurs.js";
import { ARCHETYPES, CARACTERISTIQUES, ORDRE_DES_CARACTERISTIQUES, poidsDit } from "../lexique/mots.js";
import { va } from "../routeur.js";
import { t, tn } from "../lexique/langue.js";

type Repartition = Partial<Record<Caracteristique, number>>;

function total(repartition: Repartition): number {
  return ORDRE_DES_CARACTERISTIQUES.reduce((somme, c) => somme + (repartition[c] ?? 0), 0);
}

/** Les stats telles qu'elles SERONT — base d'archétype + points, la Vie comptant quatre. Le taux
 * vient du serveur (`bareme.pvParPointDeVie`) : le recopier ici, ce serait une deuxième règle. */
function statsPrevues(base: Stats, repartition: Repartition, pvParPoint: number): Stats {
  const prevu = { ...base };
  for (const c of ORDRE_DES_CARACTERISTIQUES) {
    prevu[c] += (repartition[c] ?? 0) * (c === "vie" ? pvParPoint : 1);
  }
  return prevu;
}

export function Creation() {
  const { donnees: regles, erreur } = useRessource<Regles>("/public/regles", (charge: Regles) => charge);
  const [archetype, poseLArchetype] = useState<string | null>(null);
  const [nom, poseLeNom] = useState("");
  const [repartition, poseLaRepartition] = useState<Repartition>({});
  const [refus, poseLeRefus] = useState<string | null>(null);
  const [enCours, poseEnCours] = useState(false);

  const fiche = useMemo<FicheArchetype | null>(
    () => regles?.archetypes.find((a) => a.id === archetype) ?? null,
    [regles, archetype]
  );

  const restants = regles ? regles.pointsARepartir - total(repartition) : 0;
  const prevu = fiche && regles ? statsPrevues(fiche.base, repartition, regles.bareme.pvParPointDeVie) : null;

  function change(c: Caracteristique, delta: number) {
    if (!regles) return;
    poseLeRefus(null);
    poseLaRepartition((avant) => {
      const actuel = avant[c] ?? 0;
      const suivant = actuel + delta;
      if (suivant < 0 || suivant > regles.maxParCaracteristique) return avant;
      if (delta > 0 && total(avant) >= regles.pointsARepartir) return avant;
      return { ...avant, [c]: suivant };
    });
  }

  async function scelle() {
    if (!regles || !archetype) return;
    poseLeRefus(null);
    poseEnCours(true);
    try {
      const { heros } = await api.poste<{ heros: { id: string } }>("/heros", { nom, archetype, repartition });
      va(`/heros/${heros.id}`);
    } catch (e) {
      poseLeRefus(messageDe(e));
    } finally {
      poseEnCours(false);
    }
  }

  if (erreur) return <div className="page"><Message espece="refus">{messageDe(erreur)}</Message></div>;
  if (!regles) return <div className="page" aria-busy="true" />;

  return (
    <div className="page arrivee">
      <header className="entete-decran">
        <h1>{t("Un nouveau héros")}</h1>
        <p className="douce">
          {t("Ce que vous décidez ici, il le portera jusqu'au bout. Il ne gagnera pas de niveaux, il ne rattrapera rien : au dernier combat comme au premier, il aura exactement ces six nombres.")}
        </p>
      </header>

      <Filet>{t("D'abord, qui il est")}</Filet>

      <p className="douce" style={{ marginTop: "var(--e4)" }}>
        {t("Six façons de se tirer d'un mauvais pas. Aucune n'est meilleure : elles perdent seulement à des endroits différents.")}
      </p>

      <div className="grille grille--cartes" style={{ marginTop: "var(--e5)" }}>
        {regles.archetypes.map((a) => {
          const choisi = a.id === archetype;
          return (
            <button
              type="button"
              key={a.id}
              className={choisi ? "archetype archetype--choisi" : "archetype"}
              onClick={() => {
                poseLArchetype(a.id);
                poseLaRepartition({});
              }}
              aria-pressed={choisi}
            >
              <span className="archetype__nom">{a.nom}</span>
              <span className="archetype__devise">{ARCHETYPES[a.id]?.devise}</span>
              <span className="archetype__stats">
                {ORDRE_DES_CARACTERISTIQUES.map((c) => (
                  <span key={c}>
                    <span className="capitales">{CARACTERISTIQUES[c].abrege}</span>
                    <span className="nombre"> {a.base[c]}</span>
                  </span>
                ))}
              </span>
              <span className="archetype__capacite">
                <strong>{a.capacite.nom}</strong> — {a.capacite.description}
                {a.capacite.cout > 0 ? ` (${a.capacite.cout} d'Essence)` : ""}
              </span>
              <span className="archetype__recuperation faible">{a.recuperation}</span>
            </button>
          );
        })}
      </div>

      {fiche && prevu ? (
        <>
          <div style={{ marginTop: "var(--e7)" }}>
            <Filet>{t("Ensuite, les huit points")}</Filet>
          </div>

          <div className="creation">
            <div className="pile">
              <Parchemin variante="creux">
                <div className="rang rang--espace">
                  <h3 style={{ margin: 0 }}>{t("À répartir")}</h3>
                  <span className={restants === 0 ? "compteur compteur--fini nombre" : "compteur nombre"}>
                    {restants}
                  </span>
                </div>
                <p className="faible" style={{ marginTop: "var(--e2)", marginBottom: 0 }}>
                  {t("Quatre au maximum sur une même caractéristique. Et il faut les dépenser tous : il n'y a pas de « plus tard » où les garder.")}
                </p>
              </Parchemin>

              {ORDRE_DES_CARACTERISTIQUES.map((c) => {
                const mot = CARACTERISTIQUES[c];
                const mis = repartition[c] ?? 0;
                return (
                  <div className="caracteristique" key={c}>
                    <div className="caracteristique__tete">
                      <span className="caracteristique__nom" style={{ color: mot.teinte }}>
                        {mot.nom}
                      </span>
                      <span className="caracteristique__valeur nombre">
                        {prevu[c]}
                        {mis > 0 ? <span className="caracteristique__gain"> +{prevu[c] - fiche.base[c]}</span> : null}
                      </span>
                    </div>

                    <p className="caracteristique__consequence">
                      <strong>{mot.parPoint}</strong> — {mot.role}
                    </p>

                    <div className="caracteristique__boutons">
                      <button
                        type="button"
                        className="bouton"
                        onClick={() => change(c, -1)}
                        disabled={mis === 0}
                        aria-label={t("Retirer un point de {quoi}", { quoi: mot.nom })}
                      >
                        −
                      </button>
                      <span className="caracteristique__points nombre" aria-live="polite">
                        {mis}
                      </span>
                      <button
                        type="button"
                        className="bouton"
                        onClick={() => change(c, 1)}
                        disabled={mis >= regles.maxParCaracteristique || restants === 0}
                        aria-label={t("Ajouter un point de {quoi}", { quoi: mot.nom })}
                      >
                        +
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <aside className="creation__miroir">
              <Parchemin variante="leve" titre={t("Ce que ça donnera")}>
                <dl className="miroir">
                  <div>
                    <dt>
                      <Glyphe nom="vie" taille={15} couleur="var(--sang)" /> {t("Points de vie")}
                    </dt>
                    <dd className="nombre">{prevu.vie}</dd>
                  </div>
                  <div>
                    <dt>
                      <Glyphe nom="epee" taille={15} /> {t("Toucher")}
                    </dt>
                    <dd className="nombre">{t("d20 + {mod}", { mod: prevu.attaque })}</dd>
                  </div>
                  <div>
                    <dt>
                      <Glyphe nom="bouclier" taille={15} /> {t("On vous touche à")}
                    </dt>
                    <dd className="nombre">{regles.bareme.ddBase + prevu.defense}+</dd>
                  </div>
                  <div>
                    <dt>
                      <Glyphe nom="fuite" taille={15} /> {t("Initiative")}
                    </dt>
                    <dd className="nombre">{t("d20 + {mod}", { mod: prevu.agilite })}</dd>
                  </div>
                  <div>
                    <dt>
                      <Glyphe nom="des" taille={15} couleur="var(--or)" /> {t("Critique sur")}
                    </dt>
                    <dd className="nombre">
                      {Math.max(
                        regles.bareme.seuilCritiqueMinimum,
                        20 - Math.floor(prevu.chance / regles.bareme.chanceParPointDeCritique)
                      )}
                      +
                    </dd>
                  </div>
                  <div>
                    <dt>
                      <Glyphe nom="essence" taille={15} couleur="var(--essence)" /> {t("Essence")}
                    </dt>
                    <dd className="nombre">{prevu.essence}</dd>
                  </div>
                  <div>
                    <dt>
                      <Glyphe nom="poids" taille={15} /> {t("Portée")}
                    </dt>
                    <dd className="nombre">
                      {poidsDit(regles.bareme.capaciteBase + prevu.attaque * regles.bareme.capaciteParAttaque)}
                    </dd>
                  </div>
                </dl>

                <p className="avertissement">
                  {t("Ce n'est pas un avis, c'est ce qu'on a vu en jouant : un héros qui néglige l'Attaque perd un combat sur quatre de plus, quel que soit son archétype — elle décide si le coup passe, puis de ce qu'il coûte à l'autre. Ce héros-là, vous ne le jouerez qu'une fois.")}
                </p>
              </Parchemin>

              <Parchemin variante="pose">
                <label className="champ">
                  <span className="capitales">{t("Son nom")}</span>
                  <input
                    value={nom}
                    onChange={(e) => poseLeNom(e.target.value)}
                    maxLength={32}
                    placeholder={t("Celui qu'on lira sur la pierre")}
                  />
                </label>

                {refus ? (
                  <div style={{ marginTop: "var(--e4)" }}>
                    <Message espece="refus" surFermeture={() => poseLeRefus(null)}>
                      {refus}
                    </Message>
                  </div>
                ) : null}

                <button
                  type="button"
                  className="bouton bouton--grand"
                  style={{ marginTop: "var(--e4)", width: "100%" }}
                  disabled={enCours || restants !== 0 || nom.trim().length < 2}
                  onClick={() => void scelle()}
                >
                  {enCours
                    ? t("On grave…")
                    : restants !== 0
                      ? tn(restants, "Il reste {n} point", "Il reste {n} points")
                      : t("Sceller ce héros")}
                </button>
                <p className="faible" style={{ marginTop: "var(--e3)", marginBottom: 0, fontSize: "var(--pas-1)" }}>
                  {t("Une fois scellé, rien de tout cela ne se modifie. Jamais.")}
                </p>
              </Parchemin>
            </aside>
          </div>
        </>
      ) : null}
    </div>
  );
}
